import { apiClient } from './apiClient'

export interface OrderPayload {
  // Add order fields based on your OrderPayloadDto
  [key: string]: any
}

export interface CreateOrderResponse {
  // Add response fields based on your CreateOrderResponseDTO
  orderId?: string
  message?: string
  success: boolean
}

export interface OrderStatus {
  // Add status fields based on your OrderStatusDTO
  orderId: string
  status: string
  [key: string]: any
}

export interface ViewOrdersFilter {
  page?: number
  limit?: number
  status?: string
  // Add other filter fields
}

export const ordersService = {
  createOrder: (orderData: OrderPayload): Promise<CreateOrderResponse> =>
    apiClient.post('/orders', orderData).then(res => res.data),

  getOrders: (filters?: ViewOrdersFilter): Promise<any[]> =>
    apiClient.get('/orders', { params: filters }).then(res => res.data),

  getOrder: (orderId: string): Promise<OrderStatus> =>
    apiClient.get(`/orders/${orderId}`).then(res => res.data),

  cancelOrder: (orderId: string): Promise<any> =>
    apiClient.delete(`/orders/${orderId}`).then(res => res.data),

  getOrderStatus: (orderId: string): Promise<OrderStatus> =>
    apiClient.get(`/orders/${orderId}/status`).then(res => res.data),

  getOrdersList: (filters?: any): Promise<any[]> =>
    apiClient.get('/items', { params: filters }).then(res => res.data),

  getOrderFilters: (accountId: string): Promise<any> =>
    apiClient.get('/items/filter-options', { params: { accountId } }).then(res => res.data),

  getItemDetails: (itemId: string): Promise<any> =>
    apiClient.get(`/items/${itemId}`).then(res => res.data),

  getTrackingData: (itemId: string): Promise<any> =>
    apiClient.get(`/items/${itemId}/recipients`).then(res => {
      const recipients: any[] = res.data.recipients ?? [];
      const total: number = res.data.total ?? recipients.length;

      const ORDERED_STATUSES = [
        'In Processing',
        'In Transit',
        'In Mailstream',
        'In Destination State',
        'Out for Delivery',
        'Delivered',
        'Relabeled',
        'Marked for Return',
        'Cancelled by USPS',
      ];

      const counts = new Map<string, number>(ORDERED_STATUSES.map(s => [s, 0]));
      for (const r of recipients) {
        const s = r.trackingStatus ?? 'In Processing';
        if (counts.has(s)) {
          counts.set(s, counts.get(s)! + 1);
        }
      }

      const statusSummary = ORDERED_STATUSES.map(status => ({
        status,
        amountOfPieces: counts.get(status)!,
        percentOfMailed: total > 0 ? parseFloat(((counts.get(status)! / total) * 100).toFixed(1)) : 0,
      }));

      return {
        type: 'mailing' as const,
        totalRecipients: total,
        recipients,
        statusSummary,
      };
    }),

  getShippingTrackingData: (carrier: string, trackingNumber: string): Promise<any> =>
    apiClient
      .get(`/tracking/shipment/${carrier.toLowerCase()}/${trackingNumber}`)
      .then(res => ({ timeline: res.data.events ?? [] })),
}
